import { CalendarClock, Clock, Droplets, PackageCheck, Sun, ShieldCheck, Layers } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "../components/Reveal";

const Block = ({ icon: Icon, title, bg, children, id, wide }) => (
  <Reveal className={`h-full ${wide ? "md:col-span-2" : ""}`}>
    <section data-testid={`info-${id}`} className="h-full rounded-[28px] chunk p-6 sm:p-8" style={{ background: bg }}>
      <span className="w-12 h-12 rounded-full bg-white chunk-sm grid place-items-center mb-5"><Icon className="w-5 h-5" /></span>
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">{title}</h2>
      <div className="mt-3 space-y-3 text-base leading-relaxed">{children}</div>
    </section>
  </Reveal>
);

const tips = [
  [Sun, "Keep it cool", "PLA can soften and warp in strong heat. Keep prints out of hot cars, direct summer sun on windowsills and away from heaters."],
  [Droplets, "Gentle cleaning", "Wipe with a soft damp cloth. No dishwasher, boiling water or harsh solvents."],
  [Layers, "Layer lines are normal", "Small layer lines are part of how 3D printing works. They show your item was made layer by layer, just for you."],
  [ShieldCheck, "Decor, not dinnerware", "Our prints aren't intended for food or drink contact, and small parts aren't suitable for young children."],
];

export default function Info() {
  return (
    <main className="px-4 sm:px-8 pt-28 sm:pt-36 pb-24 layer-lines" data-testid="info-page">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <Eyebrow color="var(--lime)">Good to know</Eyebrow>
          <h1 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[0.95]">
            Returns, print times <span className="font-serif-i font-normal">& care</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#3b3550]">Everything you need to know before you order from Scratchs3dprintz.</p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
          <Block id="returns" icon={PackageCheck} title="Returns" bg="var(--yellow)">
            <p>Every print is checked to make sure it's up to our standards before it leaves us, so we don't accept change-of-mind returns. This applies to ready-made, custom and personalised items.</p>
            <p>If something arrives damaged or isn't right, email <a className="underline font-semibold" href="mailto:scratchs3dprintz@gmail.com">scratchs3dprintz@gmail.com</a> with your order number and a photo and we'll make it right.</p>
          </Block>
          <Block id="print-times" icon={Clock} title="Print times" bg="var(--cyan)">
            <p><strong>Ready-made items:</strong> 2-5 working days after your payment arrives.</p>
            <p><strong>Custom prints:</strong> 1-2 weeks, depending on size and detail. We'll confirm timing when we quote.</p>
            <p>Pickup is from Ebdentown, Upper Hutt, and we'll email you to arrange a time. Courier delivery is NZ-wide.</p>
          </Block>
          <Block id="payment-plans" icon={CalendarClock} title="Payment plans" bg="var(--lime)" wide>
            <p>Orders over $50 can be split into 2, 3 or 4 weekly payments. No interest, no fees.</p>
            <p>Your first payment is due when you order, then one payment each week. Pay by bank transfer using your order number as the reference. We'll email you a reminder the day before each payment is due, and a receipt showing what's paid and what's left each time a payment arrives.</p>
            <p><strong>Your items are printed once the full amount has been paid</strong>, then the usual print times above apply.</p>
          </Block>
        </div>

        <Reveal className="mt-16">
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl">Caring for your prints</h2>
          <p className="mt-3 max-w-2xl text-[#3b3550]">We print mostly in PLA, with some items in PETG, which is a bit tougher and more heat resistant.</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
          {tips.map(([Icon, t, d], i) => (
            <Reveal key={t} delay={i * 0.06} className="h-full">
              <div className="h-full rounded-[24px] chunk-sm bg-white p-6" data-testid={`care-tip-${i + 1}`}>
                <Icon className="w-6 h-6 text-[var(--pink)]" />
                <h3 className="font-display font-extrabold text-xl mt-3">{t}</h3>
                <p className="mt-2 text-[#3b3550]">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16">
          <div className="rounded-[28px] chunk bg-[var(--pink)] text-white p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <p className="font-display font-extrabold text-2xl sm:text-3xl">Got a question we haven't answered?</p>
            <a href="mailto:scratchs3dprintz@gmail.com" data-testid="info-email-btn" className="press chunk-sm rounded-full bg-[var(--yellow)] text-[var(--ink)] px-6 py-3 font-bold text-center">Email us</a>
          </div>
          <Link to="/" data-testid="info-back-link" className="inline-block mt-8 font-semibold underline">Back to the shop</Link>
        </Reveal>
      </div>
    </main>
  );
}
